import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainButton } from '../../Buttons/MainButton';
import LabelText from '../../FormikForm/LabelText';
import CircleLoadingIndicator from '../../LoadingIndicators/CircleLoadingIndicator';
import { USER_LOCATION_API_ERROR } from '../../../graphql/apis/user/mutations/userLocation';
import { UserLocation } from '../../../redux/user/userSlice';
import { colors } from '../../../theme/colors';
import spacing, { isMobileScreen } from '../../../theme/spacing';
import { fontSize } from '../../../theme/typography';
import { useAddUserLocationByCoordinates } from '../../../graphql/apis/userLocations/mutations/useAddUserLocationByCoordinates';
import { useUserLocation } from '../../../utils/location/hooks/useLocation';

interface AddNewUserLocationByDeviceButtonProps {
  onComplete: (newLocation: UserLocation) => void;
}

const AddNewUserLocationByDeviceButton = ({
  onComplete,
}: AddNewUserLocationByDeviceButtonProps) => {
  const { t, i18n } = useTranslation();

  const [isGettingCurrentLocation, setIsGettingCurrentLocation] =
    useState(false);

  const [isLocationAddRateLimitError, setIsLocationAddRateLimitError] =
    useState(false);

  const [isMaxLocationsReachedError, setIsMaxLocationsReachedError] =
    useState(false);

  const { getUserCurrentLocation } = useUserLocation();

  const { addUserLocation } = useAddUserLocationByCoordinates();

  const handleAddDefaultUserLocationByCoordinates = async () => {
    setIsGettingCurrentLocation(true);
    setIsLocationAddRateLimitError(false);
    const currentLocationData = await getUserCurrentLocation();

    if (!currentLocationData?.coordinates) {
      setIsGettingCurrentLocation(false);
      return;
    }

    const locationData = {
      lat: currentLocationData.coordinates.lat,
      lng: currentLocationData.coordinates.lng,
    };

    const result = await addUserLocation(locationData, onComplete, true);

    setIsGettingCurrentLocation(false);

    if (typeof result === 'string') {
      if (result === USER_LOCATION_API_ERROR.LOCATION_ADD_RATE_LIMIT) {
        setIsLocationAddRateLimitError(true);
      } else if (result === USER_LOCATION_API_ERROR.MAX_LOCATIONS_REACHED) {
        setIsMaxLocationsReachedError(true);
      }
    }
  };

  return (
    <>
      <MainButton
        onClick={handleAddDefaultUserLocationByCoordinates}
        fullWidth={isMobileScreen}
        disabled={isGettingCurrentLocation}
        endIcon={
          isGettingCurrentLocation ? (
            <CircleLoadingIndicator
              color={colors.textPrimary}
              size={fontSize.xl}
            />
          ) : null
        }
        style={{
          fontSize: isMobileScreen ? fontSize.lg : fontSize.xl,
          paddingTop: spacing.small,
          paddingBottom: spacing.small,
          width: isMobileScreen ? '100%' : 400,
          ...(isMaxLocationsReachedError ? { display: 'none' } : {}),
        }}
        text={
          isGettingCurrentLocation
            ? t('location.beingAdded')
            : t('common.allow')
        }
      />

      {isLocationAddRateLimitError && (
        <LabelText
          marginTop
          text={t('location.apiErrors.locationAddRateLimit')}
          error
        />
      )}

      {isMaxLocationsReachedError && (
        <LabelText
          marginTop
          text={t('location.maximumNumberOfLocationsAdded')}
          error
        />
      )}
    </>
  );
};

export default AddNewUserLocationByDeviceButton;
